import type { PostDynamicContentConditions } from '~/interfaces/post_dynamic_content_conditions';

interface FetchDynamicContentConditionsParams {
  postId: string;
  isDraft: boolean;
  previewJwtToken?: string;
}

/**
 * Fetches merge tags for a post from the API
 *
 * @param postId - The ID of the post
 * @param isDraft - Whether this is a draft post
 * @returns PostDynamicContentConditions object with evaluated conditions, or empty object on error
 */
export async function fetchDynamicContentConditions({
  postId,
  isDraft,
  previewJwtToken,
}: FetchDynamicContentConditionsParams): Promise<PostDynamicContentConditions> {
  try {
    const url = new URL(`/dynamic_content_conditions/${postId}`, window.location.origin);
    url.searchParams.set('draft', String(isDraft));
    if (previewJwtToken) {
      url.searchParams.set('jwt_token', previewJwtToken);
    }

    const response = await fetch(url.pathname + url.search, {
      method: 'GET',
      headers: { 'Sec-Fetch-Dest': 'empty' },
    });

    // Check if response is ok before parsing
    if (!response.ok) {
      console.debug('Failed fetching dynamic content conditions: non-ok response', response.status);
      return {};
    }

    // Validate that we have a JSON response before parsing
    const contentType = response.headers.get('content-type');
    if (!contentType || !contentType.includes('application/json')) {
      console.debug('Failed fetching dynamic content conditions: non-JSON response', contentType);
      return {};
    }

    const conditions = await response.json();
    return ('conditions' in conditions ? conditions.conditions : {}) as PostDynamicContentConditions;
  } catch (error) {
    // Don't report to Sentry - this is likely a transient network issue or invalid response
    // The breadcrumb will still be visible if this causes other errors
    console.debug('Failed fetching dynamic content conditions', error);
    return {};
  }
}
